import { useEffect, useState, useRef, useCallback } from 'react';
import io, { Socket } from 'socket.io-client';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import { trpc } from '@/trpc/client';
import { useNavigate } from 'react-router-dom';

const SOCKET_URL = (import.meta.env.VITE_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');

let socket: Socket | null = null;

declare global {
  interface Window {
    socket: Socket | undefined;
  }
}

interface LocationUpdateData {
  jobId: string;
  transporterId: string;
  latitude: number;
  longitude: number;
  timestamp: string;
}

interface UseSocketOptions {
  onLocationUpdate?: (data: LocationUpdateData) => void;
  enableDebugLogs?: boolean;
  silentMode?: boolean; // Disable toasts if true
  autoReconnect?: boolean;
}

export const useSocket = (options?: UseSocketOptions) => {
  const {
    user,
    setVerified,
    setUser,
    token,
    logout,
    trackingJobId,
    setTrackingJobId,
  } = useAuthStore();

  const [isConnected, setIsConnected] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [lastLocationUpdate, setLastLocationUpdate] =
    useState<LocationUpdateData | null>(null);

  const utils = trpc.useUtils();
  const navigate = useNavigate();

  // Store latest options in ref to avoid re-subscribing listeners
  const callbacksRef = useRef(options);

  // Update ref when options change
  useEffect(() => {
    callbacksRef.current = options;
  }, [options]);

  const socketConnectedRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const hasShownDisconnectToast = useRef(false);
  const hasShownConnectToast = useRef(false);
  const lastConnectionState = useRef<boolean | null>(null);

  const enableDebugLogs = options?.enableDebugLogs ?? false;
  const silentMode = options?.silentMode ?? true;

  const log = useCallback(
    (...args: any[]) => {
      if (enableDebugLogs) {
        console.log('[Socket.IO]', ...args);
      }
    },
    [enableDebugLogs],
  );

  const showToastOnce = useCallback(
    (
      type: 'success' | 'error' | 'warning',
      message: string,
      description?: string,
    ) => {
      if (silentMode) return;

      if (type === 'success' && !hasShownConnectToast.current) {
        hasShownConnectToast.current = true;
        hasShownDisconnectToast.current = false;
        toast.success(message, { description, duration: 2000 });
      } else if (
        (type === 'error' || type === 'warning') &&
        !hasShownDisconnectToast.current
      ) {
        hasShownDisconnectToast.current = true;
        hasShownConnectToast.current = false;
        toast.warning(message, { description, duration: 3000 });
      }
    },
    [silentMode],
  );

  // Join job room helper
  const joinJobRoom = useCallback(
    (jobId: string) => {
      if (socket && socket.connected) {
        log(`Joining job room: ${jobId}`);
        socket.emit('joinJobRoom', jobId);
        setTrackingJobId(jobId);
        socket.emit('requestJobLocation', jobId);
      } else {
        log('Cannot join job room: Socket not connected');
      }
    },
    [log, setTrackingJobId],
  );

  // Leave job room helper
  const leaveJobRoom = useCallback(
    (jobId: string) => {
      if (socket && socket.connected) {
        log(`Leaving job room: ${jobId}`);
        socket.emit('leaveJobRoom', jobId);
        setTrackingJobId(null);
      }
    },
    [log, setTrackingJobId],
  );

  // Ref to track current job ID without triggering re-renders/re-connections
  const trackingJobIdRef = useRef(trackingJobId);

  useEffect(() => {
    trackingJobIdRef.current = trackingJobId;
  }, [trackingJobId]);

  useEffect(() => {
    if (user && token && !socketConnectedRef.current) {
      log('Initializing Socket.IO connection to:', SOCKET_URL);

      if (!socket || !socket.connected) {
        socket = io(SOCKET_URL, {
          reconnection: true,
          reconnectionAttempts: 20,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          timeout: 20000,
          transports: ['websocket', 'polling'],
          auth: { token },
          path: '/socket.io/',
        });
        window.socket = socket;
      } else {
        socket.io.opts.auth = { token };
      }

      // Heartbeat every 20s
      const heartbeat = setInterval(() => {
        if (socket?.connected) socket.emit('ping');
      }, 20000);

      socket.on('pong', () => {
        log('Heartbeat pong received');
      });

      // Event handlers
      const onConnect = () => {
        log('Connected with socket ID:', socket?.id);
        setIsConnected(true);
        socketConnectedRef.current = true;
        setConnectionAttempts(0);

        if (lastConnectionState.current === false) {
          showToastOnce('success', 'Connection restored');
        }
        lastConnectionState.current = true;

        if (user?.id && socket) {
          socket.emit('registerUser', user.id);
          log('Registered user:', user.id);
          // Use Ref to get latest ID without dependency
          if (trackingJobIdRef.current) {
            joinJobRoom(trackingJobIdRef.current);
          }
        }
      };

      const onDisconnect = (reason: string) => {
        console.warn('> Socket disconnected:', reason);
        setIsConnected(false);
        socketConnectedRef.current = false;

        if (lastConnectionState.current === true) {
          if (reason === 'io server disconnect') {
            if (!silentMode && token) {
              toast.error('Session expired. Please log in again.');
              logout();
            }
          } else {
            showToastOnce('warning', 'Connection lost', 'Reconnecting...');
            // Don't nullify tracking ID on disconnect, so we can rejoin on reconnect
            // if (trackingJobId) {
            //   setTrackingJobId(null);
            // }
          }
        }
        lastConnectionState.current = false;
      };

      const onConnectError = (error: Error) => {
        console.error('> Connection error:', error.message);
        setConnectionAttempts((prev) => prev + 1);
        setIsConnected(false);
        socketConnectedRef.current = false;
      };

      const onReconnectAttempt = (attempt: number) => {
        log(`Reconnection attempt ${attempt} ...`);
        setConnectionAttempts(attempt);
      };

      const onReconnect = (attempt: number) => {
        log(`Reconnected after ${attempt} attempts`);
        showToastOnce('success', 'Reconnected');
        setConnectionAttempts(0);

        // Re-register and re-join job if needed
        if (user?.id) {
          socket?.emit('registerUser', user.id);
          if (trackingJobIdRef.current) joinJobRoom(trackingJobIdRef.current);
        }
      };

      const onReconnectFailed = () => {
        console.error('X All reconnection attempts failed');
        if (!silentMode) {
          toast.error('Connection failed. Please refresh the page.', {
            duration: 10000,
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload(),
            },
          });
        }
      };

      const onVerificationSuccess = (data: { message: string }) => {
        toast.success('Account Verified!', {
          description: data.message,
          duration: 5000,
        });
        setVerified();
        utils.user.getProfile.invalidate();
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      };

      const onVerificationRejected = (data: { message: string }) => {
        toast.error('Verification Rejected', {
          description: data.message,
          duration: 8000,
          action: {
            label: 'Re-submit',
            onClick: () => navigate('/onboarding/verify'),
          },
        });
        utils.user.getProfile.invalidate();
      };

      const onUserStatusChanged = (data: any) => {
        if (data && data.id === user?.id) {
          setUser(data);
          utils.user.getProfile.invalidate();
        }
      };

      const onNewNotification = (data: {
        title: string;
        message: string;
        link?: string;
        type?: 'info' | 'success' | 'warning' | 'error';
      }) => {
        utils.notification.getMyNotifications.invalidate();
        const toastFn =
          data.type === 'error'
            ? toast.error
            : data.type === 'warning'
              ? toast.warning
              : data.type === 'success'
                ? toast.success
                : toast.info;

        toastFn(data.title, {
          description: data.message,
          duration: 5000,
          action: data.link
            ? {
              label: 'View',
              onClick: () => navigate(data.link!),
            }
            : undefined,
        });
      };

      const onJobLocationUpdate = (data: LocationUpdateData) => {
        if (!data) {
          log('Received empty location update');
          return;
        }
        log('Location update received:', {
          jobId: data.jobId,
          lat: data.latitude?.toFixed(5) ?? 'N/A',
          lng: data.longitude?.toFixed(5) ?? 'N/A',
          timestamp: new Date(data.timestamp).toLocaleTimeString(),
        });

        setLastLocationUpdate(data);
        if (callbacksRef.current?.onLocationUpdate) {
          callbacksRef.current.onLocationUpdate(data);
        }

        const event = new CustomEvent('jobLocationUpdate', { detail: data });
        window.dispatchEvent(event);
      };

      const onJobRoomJoined = (data: { jobId: string; success: boolean }) => {
        if (data.success) {
          log(`Successfully joined job room: ${data.jobId}`);
          if (!silentMode) {
            // Optional: toast.success('Tracking started');
          }
        } else {
          console.error(`Failed to join job room: ${data.jobId}`);
          if (!silentMode) {
            toast.error('Failed to start tracking', { duration: 3000 });
          }
        }
      };

      const onJobRoomLeft = (data: { jobId: string }) => {
        log(`Left job room: ${data.jobId}`);
        setLastLocationUpdate(null);
      };

      const onInitialLocation = (data: LocationUpdateData | null) => {
        if (data) {
          log('Received initial location:', data);
          setLastLocationUpdate(data);
          callbacksRef.current?.onLocationUpdate?.(data);
        } else {
          log('No initial location available');
        }
      };

      // Register listeners safely
      const cleanupListeners = () => {
        if (!socket) return;
        socket.off('connect', onConnect);
        socket.off('disconnect', onDisconnect);
        socket.off('connect_error', onConnectError);
        socket.off('reconnect_attempt', onReconnectAttempt);
        socket.off('reconnect', onReconnect);
        socket.off('reconnect_failed', onReconnectFailed);
        socket.off('verificationSuccess', onVerificationSuccess);
        socket.off('verificationRejected', onVerificationRejected);
        socket.off('userStatusChanged', onUserStatusChanged);
        socket.off('new_notification', onNewNotification);
        socket.off('jobLocationUpdate', onJobLocationUpdate);
        socket.off('jobRoomJoined', onJobRoomJoined);
        socket.off('jobRoomLeft', onJobRoomLeft);
        socket.off('initialLocation', onInitialLocation);
        socket.off('pong');
      };

      // Clean up potentially stale listeners from previous effect runs
      cleanupListeners();

      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('connect_error', onConnectError);
      socket.on('reconnect_attempt', onReconnectAttempt);
      socket.on('reconnect', onReconnect);
      socket.on('reconnect_failed', onReconnectFailed);
      socket.on('verificationSuccess', onVerificationSuccess);
      socket.on('verificationRejected', onVerificationRejected);
      socket.on('userStatusChanged', onUserStatusChanged);
      socket.on('new_notification', onNewNotification);
      socket.on('jobLocationUpdate', onJobLocationUpdate);
      socket.on('jobRoomJoined', onJobRoomJoined);
      socket.on('jobRoomLeft', onJobRoomLeft);
      socket.on('initialLocation', onInitialLocation);
      socket.on('pong', () => log('Heartbeat pong received'));

      // Connect
      if (user && token && !socket.connected) {
        log('Connecting ...');
        socket.connect();
      }

      // Cleanup
      return () => {
        log('Cleaning up Socket.IO listeners ...');
        clearInterval(heartbeat);
        cleanupListeners();

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
    } else if (!user || !token) {
      if (socket?.connected) {
        log('Disconnecting due to no user/token ...');
        socket.disconnect();
      }
      socketConnectedRef.current = false;
      setIsConnected(false);
      if (window.socket) {
        delete (window as any).socket;
      }
    }

    if (socket?.io?.opts) {
      socket.io.opts.auth = { token: token || null };
    }
  }, [
    user,
    token,
    setVerified,
    utils,
    navigate,
    logout,
    // trackingJobId, // REMOVED to decouple connection logic from room logic
    // setTrackingJobId, // REMOVED
    log,
    joinJobRoom,
    // options,
    showToastOnce,
    silentMode,
  ]);

  return {
    socket,
    isConnected,
    connectionAttempts,
    lastLocationUpdate,
    joinJobRoom,
    leaveJobRoom,
  };
};
